import { useState, useRef, useEffect } from 'react';
import { Link } from "react-router-dom";
import NotificationsDropdown from "./NotificationsDropdown";
import { useTheme } from '../hooks/useTheme';
import { isAuthenticatedUser } from '../utils/authUser';
import { LogOut, User, Moon, Sun, Menu, X } from "lucide-react";
import "../assets/css/App.css";

export default function Navbar({ user, isLoading, onLogout, onMenuClick, mobileNavOpen }) {
    const { isDarkMode, toggleDarkMode } = useTheme();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const dropdownRef = useRef(null);
    const profileLink = user?.isCoach ? "/coaching/profile" : "/preferences";
    // Regular users have Notifications in the mobile bottom bar, so the top-bar
    // bell is redundant for them on small screens.
    const isRegularUser = user && !user.isAdmin && !user.isCoach && !user.isEmployer;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (isLoading) return <nav className={`navbar ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} style={{ height: '80px' }}></nav>;

    if (!isAuthenticatedUser(user)) {
        return null;
    }

    return (
        <nav className={`sticky top-0 z-[110] flex justify-between items-center px-2 md:px-8 py-2 md:py-4 border-b min-h-[56px] ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        }`} style={{
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            borderBottomColor: isDarkMode ? '#334155' : '#e2e8f0'
        }}>
            {/* LEFT SIDE: Mobile menu button + Branding */}
            <div className="flex items-center gap-1 md:gap-3">
                {onMenuClick && (
                    <button
                        onClick={onMenuClick}
                        aria-label={mobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
                        aria-expanded={mobileNavOpen}
                        className={`md:hidden p-2 -ml-1 rounded-lg transition-colors ${
                            isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        {mobileNavOpen ? <X size={26} /> : <Menu size={26} />}
                    </button>
                )}
                <Link to="/" className="flex items-center gap-2 md:gap-3 no-underline">
                    <img src="/BizBrandIcon.jpg" alt="Logo" className="w-9 h-9 md:w-10 md:h-10 rounded-xl shadow-sm" />
                    <span className={`text-lg md:text-2xl font-extrabold tracking-tight ${
                        isDarkMode ? 'text-slate-100' : 'text-slate-900'
                    }`}>
                        My BizBrand
                    </span>
                </Link>
            </div>

            {/* RIGHT SIDE: Utilities and User */}
            <div className="flex items-center gap-4 md:gap-8">
                {user ? (
                    <>
                        {/* Theme Toggle */}
                        {/* <button
                            onClick={toggleDarkMode}
                            className={`p-2 rounded-lg transition-colors ${
                                isDarkMode 
                                    ? 'bg-slate-700 text-amber-300 hover:bg-slate-600' 
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        >
                            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button> */}
                        <div className={isRegularUser ? 'hidden md:block' : ''}>
                            <NotificationsDropdown />
                        </div>

                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsProfileOpen(!isProfileOpen)}
                                aria-label="Open account menu"
                                className={`flex items-center p-1 rounded-full transition-all duration-200 border-none bg-transparent cursor-pointer group ${
                                    isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'
                                }`}
                            >
                                {user.profilePicture ? (
                                    <img
                                        src={user.profilePicture}
                                        alt="Profile"
                                        className="w-10 h-10 rounded-full object-cover shadow-md group-hover:scale-105 transition-transform border-2 border-slate-200"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold shadow-md group-hover:scale-105 transition-transform">
                                        {user.first_name ? user.first_name[0] : 'U'}
                                    </div>
                                )}
                            </button>

                            {/* USER DROPDOWN POPUP */}
                            {isProfileOpen && (
                                <>
                                <div
                                    className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm"
                                    onClick={() => setIsProfileOpen(false)}
                                />
                                <div className={`absolute right-0 mt-3 w-56 border rounded-2xl shadow-xl py-3 z-[70] animate-in fade-in slide-in-from-top-2 ${
                                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                }`}>
                                    <div className="px-4 py-2 mb-2">
                                        <p className={`text-xs font-semibold uppercase tracking-wider ${
                                            isDarkMode ? 'text-slate-400' : 'text-slate-400'
                                        }`}>User Menu</p>
                                    </div>
                                    <Link
                                        to={profileLink}
                                        className={`flex items-center gap-3 px-4 py-3 text-md no-underline transition-colors ${
                                            isDarkMode 
                                                ? 'text-slate-200 hover:bg-slate-700' 
                                                : 'text-slate-700 hover:bg-slate-50'
                                        }`}
                                        onClick={() => setIsProfileOpen(false)}
                                    >
                                        <User size={18} className={isDarkMode ? 'text-slate-400' : 'text-slate-400'} />
                                        My Profile
                                    </Link>
                                    <hr className={`my-2 ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`} />
                                    <button
                                        onClick={onLogout}
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-md border-none bg-transparent cursor-pointer font-bold transition-colors ${
                                            isDarkMode 
                                                ? 'text-red-400 hover:bg-red-900/20' 
                                                : 'text-red-600 hover:bg-red-50'
                                        }`}
                                    >
                                        <LogOut size={18} />
                                        Sign Out
                                    </button>
                                </div>
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/contact" 
                            className={`font-semibold no-underline hover:text-blue-600 transition-colors ${
                                isDarkMode ? 'text-slate-300' : 'text-slate-600'
                            }`}
                        >
                            Contact Us
                        </Link>
                        <Link to="/login" className="bg-blue-600 text-white px-6 py-2.5 rounded-full font-bold no-underline hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all">
                            Sign In
                        </Link>
                    </div>
                )}
            </div>
        </nav>
    );
}